// Variant preview component for experiment details
import { Tag, Title, Text } from '@nimbus-ds/components'
import { MobileIcon, DesktopIcon } from '@nimbus-ds/icons'
import type { Experiment, Tier1VisualConfig } from '@/types'
import { displayUrlPattern } from '@/lib/urlPattern'

interface VariantPreviewProps {
  experiment: Experiment
  variant: 'control' | 'treatment'
}

export function VariantPreview({ experiment, variant }: VariantPreviewProps) {
  const isControl = variant === 'control'
  const variantData = isControl ? experiment.variants?.control : experiment.variants?.treatment

  // No dedicated tablet glyph in Nimbus icons — tablet reuses the mobile icon.
  const getDeviceIcon = (device?: string) => {
    switch (device) {
      case 'mobile':
        return <MobileIcon size={12} />
      case 'tablet':
        return <MobileIcon size={12} />
      case 'desktop':
        return <DesktopIcon size={12} />
      default:
        return null
    }
  }

  const renderPreview = () => {
    switch (experiment.type) {
      case 'tier1_visual': {
        const config = variantData?.config as Tier1VisualConfig | undefined

        if (isControl) {
          return (
            <div className="text-center py-8">
              <Text>Versión original sin modificaciones</Text>
            </div>
          )
        }

        if (!config || (!config.modifications?.length && !config.elements_to_hide?.length && !config.captured_events?.length)) {
          return (
            <div className="text-center py-8">
              <Text>No hay modificaciones visuales configuradas</Text>
            </div>
          )
        }

        return (
          <div className="space-y-4">
            {/* Visual Modifications */}
            {config.modifications && config.modifications.length > 0 && (
              <div>
                <Title as="h5" className="mb-2">Modificaciones de Estilo ({config.modifications.length})</Title>
                <div className="space-y-2">
                  {config.modifications.slice(0, 3).map((mod, i) => (
                    <div key={i} className="bg-muted p-2 rounded">
                      <div className="flex items-center gap-2 mb-1">
                        <code className="text-xs font-mono">{mod.selector}</code>
                        {mod.device && mod.device !== 'all' && (
                          <div className="shrink-0 whitespace-nowrap">
                            <Tag appearance="neutral">
                              {getDeviceIcon(mod.device)}
                              <span className="ml-1">{mod.device}</span>
                            </Tag>
                          </div>
                        )}
                      </div>
                      {mod.urlPattern && mod.urlPattern !== '/*' && (
                        <Text fontSize="caption">
                          Página: <code className="bg-background px-1 rounded">{displayUrlPattern(mod.urlPattern)}</code>
                        </Text>
                      )}
                      {mod.changes && Object.keys(mod.changes).length > 0 && (
                        <Text fontSize="caption" className="mt-1">
                          {Object.keys(mod.changes).length} cambio(s) CSS
                        </Text>
                      )}
                    </div>
                  ))}
                  {config.modifications.length > 3 && (
                    <Text fontSize="caption" className="text-center py-1">
                      +{config.modifications.length - 3} modificaciones más
                    </Text>
                  )}
                </div>
              </div>
            )}

            {/* Hidden Elements */}
            {config.elements_to_hide && config.elements_to_hide.length > 0 && (
              <div>
                <Title as="h5" className="mb-2">Elementos Ocultos ({config.elements_to_hide.length})</Title>
                <div className="space-y-2">
                  {config.elements_to_hide.slice(0, 2).map((elem, i) => (
                    <div key={i} className="bg-muted p-2 rounded">
                      <div className="flex items-center gap-2 mb-1">
                        <code className="text-xs font-mono">{elem.selector}</code>
                        <div className="shrink-0 whitespace-nowrap">
                          <Tag appearance={elem.hideIn === 'treatment' ? 'primary' : 'neutral'}>
                            {elem.hideIn === 'treatment' ? 'Variante' : 'Control'}
                          </Tag>
                        </div>
                        {elem.device && elem.device !== 'all' && (
                          <div className="shrink-0 whitespace-nowrap">
                            <Tag appearance="neutral">
                              {getDeviceIcon(elem.device)}
                              <span className="ml-1">{elem.device}</span>
                            </Tag>
                          </div>
                        )}
                      </div>
                      {elem.urlPattern && elem.urlPattern !== '/*' && (
                        <Text fontSize="caption">
                          Página: <code className="bg-background px-1 rounded">{displayUrlPattern(elem.urlPattern)}</code>
                        </Text>
                      )}
                    </div>
                  ))}
                  {config.elements_to_hide.length > 2 && (
                    <Text fontSize="caption" className="text-center py-1">
                      +{config.elements_to_hide.length - 2} elementos más
                    </Text>
                  )}
                </div>
              </div>
            )}

            {/* Captured Events */}
            {config.captured_events && config.captured_events.length > 0 && (
              <div>
                <Title as="h5" className="mb-2">Eventos Capturados ({config.captured_events.length})</Title>
                <div className="bg-muted p-2 rounded">
                  <Text fontSize="caption">
                    {config.captured_events.length} evento(s) personalizado(s) capturado(s)
                  </Text>
                </div>
              </div>
            )}
          </div>
        )
      }

      default:
        return (
          <div className="text-center py-8">
            <Text>Vista previa no disponible para {experiment.type}</Text>
          </div>
        )
    }
  }

  return (
    <div className="space-y-3">
      <Text fontSize="caption">
        {variantData?.name || (isControl ? 'Control' : 'Treatment')}
      </Text>
      {renderPreview()}
    </div>
  )
}
